// src/components/multi-select.tsx

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import {
  CheckIcon,
  XCircle,
  ChevronDown,
  XIcon,
  WandSparkles,
} from "lucide-react";

import { cn } from "src/lib/utils";
import { Separator } from "src/components/ui/separator";
import { Button } from "src/components/ui/button";
import { Badge } from "src/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "src/components/ui/command";
import { SelectOptionsType } from "src/types";
import { CustomInputOptions } from "../CustomInput";
import { USER_TYPE_BACKGROUND } from "src/constant";
import { capitalize } from "src/utils/commons";
import { getSalespersonContractPeriod } from "src/pages/UserManagement/SalespersonList/utils/getSalespersonContractPeriod";

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva(
  "m-1 transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 duration-300",
  {
    variants: {
      variant: {
        default:
          "border-foreground/10 text-foreground bg-card hover:bg-card/80",
        secondary:
          "border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        inverted: "inverted",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectVariants> {
  /**
   * An array of option objects to be displayed in the multi-select component.
   * Each option object has a label, value, and an optional icon.
   */
  options: CustomInputOptions[];

  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  onValueChange: (value: string[]) => void;

  /** The default selected values when the component mounts. */
  defaultValue: string[];
  selectedOptions: SelectOptionsType;

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string;

  /**
   * Animation duration in seconds for the visual effects (e.g., bouncing badges).
   * Optional, defaults to 0 (no animation).
   */
  animation?: number;

  /**
   * Maximum number of items to display. Extra selected items will be summarized.
   * Optional, defaults to 3.
   */
  maxCount?: number;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * If true, renders the multi-select component as a child of another component.
   * Optional, defaults to false.
   */
  asChild?: boolean;

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string;

  label?: string;

  searchValue?: string;
  onSearch?: (search: string) => void;
  normalHeight?: boolean;
  disableSelect?: boolean;
}

export const MultiSelect = React.forwardRef<
  HTMLButtonElement,
  MultiSelectProps
>(
  (
    {
      options,
      onValueChange,
      variant,
      defaultValue = [],
      selectedOptions = [],
      placeholder = "Select options",
      animation = 0,
      maxCount = 3,
      modalPopover = false,
      asChild = false,
      className,
      label,
      searchValue,
      onSearch,
      normalHeight,
      disableSelect,
      ...props
    },
    ref
  ) => {
    const [selectedValues, setSelectedValues] =
      React.useState<string[]>(defaultValue);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [isAnimating, setIsAnimating] = React.useState(false);

    const allOptions = [...options, ...selectedOptions];

    React.useEffect(() => {
      setSelectedValues(defaultValue);
    }, [defaultValue]);

    const handleInputKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (event.key === "Enter") {
        setIsPopoverOpen(true);
      } else if (event.key === "Backspace" && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const toggleOption = (value: string) => {
      const newSelectedValues = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const handleClear = () => {
      setSelectedValues([]);
      onValueChange([]);

      if (onSearch) {
        onSearch("");
      }
    };

    const handleTogglePopover = () => {
      setIsPopoverOpen((prev) => !prev);
    };

    const clearExtraOptions = () => {
      const newSelectedValues = selectedValues.slice(0, maxCount);
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const toggleAll = () => {
      if (selectedValues.length === options.length) {
        handleClear();
      } else {
        const allValues = options
          .filter((option) => option?.isSalespersonAssigned !== true)
          .map((option) => `${option.value}`);

        setSelectedValues(allValues);
        onValueChange(allValues);
      }
    };

    return (
      <Popover
        open={disableSelect ? false : isPopoverOpen}
        onOpenChange={(open) => {
          if (!disableSelect) setIsPopoverOpen(open);
        }}
      >
        <div>
          {label && (
            <div className="flex flex-row items-center justify-between">
              <span className="text-sm text-darkRed-70 font-semibold">
                {label}
              </span>

              <Button variant="link" onClick={handleClear}>
                <span className="text-sm text-darkRed-50 font-semibold">
                  Clear
                </span>
              </Button>
            </div>
          )}

          <PopoverTrigger asChild>
            <div>
              <Button
                ref={ref}
                {...props}
                onClick={handleTogglePopover}
                disabled={disableSelect}
                className={cn(
                  `flex w-full px-0 py-3 rounded-md border ${
                    normalHeight
                      ? selectedValues.length > 0
                        ? "py-1.5"
                        : "py-2.5"
                      : "min-h-20"
                  } h-auto items-start justify-between bg-white hover:bg-white`,
                  className
                )}
              >
                {selectedValues.length > 0 ? (
                  <div className="flex justify-between items-center w-full px-2">
                    <div className="flex flex-wrap items-center">
                      {selectedValues.slice(0, maxCount).map((value) => {
                        const option = allOptions.find(
                          (o) => o.value === value
                        );
                        // const IconComponent = option?.icon;
                        return (
                          <Badge
                            key={value}
                            className={cn(
                              isAnimating ? "animate-bounce" : "",
                              multiSelectVariants({ variant })
                            )}
                            style={{ animationDuration: `${animation}s` }}
                          >
                            {/* {IconComponent && (
                              <IconComponent className="h-4 w-4 mr-2" />
                            )} */}
                            {option?.label}
                            <XCircle
                              className="ml-2 h-4 w-4 cursor-pointer"
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleOption(value);
                              }}
                            />
                          </Badge>
                        );
                      })}
                      {selectedValues.length > maxCount && (
                        <Badge
                          className={cn(
                            "bg-transparent text-foreground border-foreground/1 hover:bg-transparent",
                            isAnimating ? "animate-bounce" : "",
                            multiSelectVariants({ variant })
                          )}
                          style={{ animationDuration: `${animation}s` }}
                        >
                          {`+ ${selectedValues.length - maxCount} more`}
                          <XCircle
                            className="ml-2 h-4 w-4 cursor-pointer"
                            onClick={(event) => {
                              event.stopPropagation();
                              clearExtraOptions();
                            }}
                          />
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <XIcon
                        className="h-4 mx-2 cursor-pointer text-muted-foreground"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleClear();
                        }}
                      />
                      <Separator
                        orientation="vertical"
                        className="flex min-h-6 h-full"
                      />
                      <ChevronDown className="h-4 mx-2 cursor-pointer text-muted-foreground" />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between w-full mx-auto">
                    <span className="text-sm font-normal text-muted-foreground mx-3">
                      {placeholder}
                    </span>
                    <ChevronDown className="h-4 cursor-pointer text-muted-foreground mx-2" />
                  </div>
                )}
              </Button>
            </div>
          </PopoverTrigger>
        </div>
        <PopoverContent
          className="w-auto p-0"
          align="start"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command>
            <CommandInput
              placeholder="Search..."
              value={searchValue}
              onKeyDown={handleInputKeyDown}
              onValueChange={onSearch}
            />
            <CommandList onWheel={(e) => e.stopPropagation()}>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  key="all"
                  onSelect={toggleAll}
                  className="cursor-pointer"
                >
                  <div
                    className={cn(
                      "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-darkIndicator-30",
                      selectedValues.length === options.length
                        ? "bg-darkBrown-50 text-white"
                        : "opacity-50 [&_svg]:invisible"
                    )}
                  >
                    <CheckIcon className="h-4 w-4" />
                  </div>
                  <span>(Select All)</span>
                </CommandItem>
                {options.map((option) => {
                  const value = `${option.value}`;

                  const userType = option?.userType;
                  const customerType = option.customerType;
                  const customerTypeHex = option?.typeHex;
                  const isSalespersonAssigned = option.isSalespersonAssigned;
                  const activeContracts = option.activeContracts;
                  const isSelected = selectedValues.includes(value);

                  return (
                    <CommandItem
                      value={option.label}
                      key={option.value}
                      onSelect={() => toggleOption(value)}
                      className={cn(
                        "w-full flex justify-between gap-4 cursor-pointer"
                      )}
                    >
                      <div className="flex flex-1 flex-row items-center">
                        <div
                          className={cn(
                            "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-darkIndicator-30",
                            isSelected
                              ? "bg-darkBrown-50 text-white"
                              : "opacity-50 [&_svg]:invisible"
                          )}
                        >
                          <CheckIcon className="h-4 w-4" />
                        </div>

                        <span>{option.label}</span>
                      </div>

                      <div className="flex flex-col items-end gap-2">
                        {(userType || isSalespersonAssigned) && (
                          <div
                            className="font-normal text-xs px-1 rounded text-right max-w-[200px]"
                            style={{
                              backgroundColor: isSalespersonAssigned
                                ? "#D4D4D4"
                                : userType === "customer"
                                ? customerTypeHex
                                : userType === "partner"
                                ? USER_TYPE_BACKGROUND.Partner
                                : USER_TYPE_BACKGROUND.Vendor,
                            }}
                          >
                            {isSalespersonAssigned
                              ? "Has an active salesperson"
                              : userType === "customer"
                              ? customerType
                              : capitalize(userType)}
                          </div>
                        )}

                        {isSalespersonAssigned && (
                          <div className="max-w-56 text-xs text-muted-foreground text-right">
                            {activeContracts?.map((activeContract, index) => (
                              <span key={index}>{`${
                                index !== 0 ? "and " : ""
                              }From ${getSalespersonContractPeriod(
                                activeContract.contract_start_date,
                                activeContract.contract_end_date,
                                true
                              )} `}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <div className="flex items-center justify-between">
                  {selectedValues.length > 0 && (
                    <>
                      <CommandItem
                        onSelect={handleClear}
                        className="flex-1 justify-center cursor-pointer"
                      >
                        Clear
                      </CommandItem>
                      <Separator
                        orientation="vertical"
                        className="flex min-h-6 h-full"
                      />
                    </>
                  )}
                  <CommandItem
                    onSelect={() => setIsPopoverOpen(false)}
                    className="flex-1 justify-center cursor-pointer max-w-full"
                  >
                    Close
                  </CommandItem>
                </div>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
        {animation > 0 && selectedValues.length > 0 && (
          <WandSparkles
            className={cn(
              "cursor-pointer my-2 text-foreground bg-background w-3 h-3",
              isAnimating ? "" : "text-muted-foreground"
            )}
            onClick={() => setIsAnimating(!isAnimating)}
          />
        )}
      </Popover>
    );
  }
);

MultiSelect.displayName = "MultiSelect";
